import { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { Settings as SettingsIcon, LogOut, User } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { useI18n } from "@/context/I18n";

/**
 * Avatar that opens the account actions.
 *
 * On a phone there is no room for a row of icons next to the search box: the
 * profile and logout buttons were being pushed off the right edge of a 390px
 * screen. Collapsing them behind the avatar keeps them reachable at any width.
 */
export default function AccountMenu({ user, onLogout }) {
  const { t, lang, toggle } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const away = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    const key = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", key);
    };
  }, [open]);

  const initial = (user?.name || "U")[0].toUpperCase();
  const itemCls =
    "w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-zinc-300 hover:text-white hover:bg-white/10 transition-colors";

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="account-menu-btn"
        aria-label={t("nav_profile")}
        className={`w-9 h-9 rounded-full bg-[#FF2A4D] flex items-center justify-center text-xs font-bold overflow-hidden transition ${
          open ? "ring-2 ring-white/40" : "hover:brightness-110"
        }`}
      >
        {user?.avatar_url ? (
          <img src={mediaUrl(user.avatar_url)} alt="" className="w-full h-full object-cover" />
        ) : (
          initial
        )}
      </button>

      {open && (
        <div
          data-testid="account-menu"
          className="absolute right-0 top-full mt-2 w-56 bg-[#1a1a1a] border border-white/10 rounded-xl shadow-2xl p-2 z-[70]"
        >
          <div className="px-3 py-2 border-b border-white/10 mb-1">
            <p className="text-sm font-semibold truncate">{user?.name}</p>
            <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
          </div>

          <NavLink
            to="/app/settings"
            onClick={() => setOpen(false)}
            data-testid="menu-profile"
            className={itemCls}
          >
            <User className="w-4 h-4" /> {t("nav_profile")}
          </NavLink>

          <NavLink
            to="/app/settings"
            onClick={() => setOpen(false)}
            data-testid="menu-settings"
            className={itemCls}
          >
            <SettingsIcon className="w-4 h-4" /> {t("nav_settings")}
          </NavLink>

          <button onClick={toggle} data-testid="menu-language" className={itemCls}>
            <span className="w-4 text-center text-[11px] font-bold">{lang === "es" ? "ES" : "EN"}</span>
            {lang === "es" ? "English" : "Español"}
          </button>

          <button
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
            data-testid="menu-logout"
            className={`${itemCls} text-zinc-400 hover:text-red-400`}
          >
            <LogOut className="w-4 h-4" /> {t("logout")}
          </button>
        </div>
      )}
    </div>
  );
}
